// src/hooks/useReturnRequests.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ReturnRequestService,
  ReturnStatus,
  RefundStatus,
  RefundMethod,
} from '../admin/pages/return/returnRequestService';

export const RETURN_KEYS = {
  all: ['returnRequests'] as const,
  lists: () => ['returnRequests', 'list'] as const,
  paginated: (page: number, search: string, status: string, reason: string) =>
    ['returnRequests', 'list', page, search, status, reason] as const,
  stats: () => ['returnRequests', 'stats'] as const,
};

export interface ReturnPaginationParams {
  page: number;
  search: string;
  status: string;
  reason: string;
  pageSize: number;
}

export const useReturnRequestsPaginated = (params: ReturnPaginationParams) => {
  return useQuery({
    queryKey: RETURN_KEYS.paginated(params.page, params.search, params.status, params.reason),
    queryFn: () =>
      ReturnRequestService.getReturnRequestsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? (params.status as ReturnStatus) : 'all',
        params.reason !== 'all' ? params.reason : undefined
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useReturnStats = () => {
  return useQuery({
    queryKey: RETURN_KEYS.stats(),
    queryFn: () => ReturnRequestService.getStatusCounts(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useReturnMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: RETURN_KEYS.all });
  };

  const approve = useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes?: string }) =>
      ReturnRequestService.approveReturnRequest(requestId, adminNotes),
    onSuccess: invalidateAll,
  });

  const reject = useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes?: string }) =>
      ReturnRequestService.rejectReturnRequest(requestId, adminNotes),
    onSuccess: invalidateAll,
  });

  const complete = useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes?: string }) =>
      ReturnRequestService.completeReturnRequest(requestId, adminNotes),
    onSuccess: invalidateAll,
  });

  const saveNotes = useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes: string }) =>
      ReturnRequestService.updateAdminNotes(requestId, adminNotes),
  });

  const saveRefund = useMutation({
    mutationFn: ({
      requestId,
      details,
    }: {
      requestId: string;
      details: {
        refundStatus: RefundStatus;
        refundedAmount: number;
        refundMethod: RefundMethod;
        refundTransactionId?: string;
        refundDate?: Date;
        refundNotes?: string;
      };
    }) => ReturnRequestService.updateRefundDetails(requestId, details),
    onSuccess: invalidateAll,
  });

  // ── Delete ────────────────────────────────────────────────────────────────
  const remove = useMutation({
    mutationFn: (requestId: string) =>
      ReturnRequestService.deleteReturnRequest(requestId),
    onSuccess: invalidateAll,
  });

  return { approve, reject, complete, saveNotes, saveRefund, remove };
};